import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { formatEuro, formatDateFr } from "@/lib/format";
import { ArrowRight, Coins } from "lucide-react";
import { FLOW_NATURE_LABELS, FLOW_STATUS_LABELS } from "@/constants/group";

export default function CompanyFlowsTab({ company }) {
  const [flows, setFlows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get(`/intragroup-flows?company_id=${company.id}`), api.get("/companies")])
      .then(([f, c]) => { setFlows(f.data.items || []); setCompanies(c.data.items || []); })
      .finally(() => setLoading(false));
  }, [company.id]);

  const name = (id) => companies.find((c) => c.id === id)?.denomination || "—";

  const outgoing = flows.filter((f) => f.from_company_id === company.id);
  const incoming = flows.filter((f) => f.to_company_id === company.id);

  const sum = (arr) => arr.reduce((a, f) => a + Number(f.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <StatCard label="Flux entrants" amount={sum(incoming)} count={incoming.length} tone="success" />
        <StatCard label="Flux sortants" amount={sum(outgoing)} count={outgoing.length} tone="info" />
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="px-6 py-4 border-b border-border font-heading">Historique des flux</div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Sens</th>
              <th className="px-6 py-3">Contrepartie</th>
              <th className="px-6 py-3">Nature</th>
              <th className="px-6 py-3 text-right">Montant</th>
              <th className="px-6 py-3">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && flows.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                <Coins className="mx-auto h-6 w-6 mb-2 text-muted-foreground/60" /> Aucun flux enregistré.
              </td></tr>
            )}
            {flows.map((f) => {
              const isOut = f.from_company_id === company.id;
              return (
                <tr key={f.id} className="hover:bg-secondary/30">
                  <td className="px-6 py-3 text-xs font-mono text-muted-foreground">{formatDateFr(f.date)}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${isOut ? "text-amber-800" : "text-emerald-800"}`}>
                      {isOut ? "Sortant" : "Entrant"} <ArrowRight className="h-3 w-3" />
                    </span>
                  </td>
                  <td className="px-6 py-3 font-medium">{isOut ? name(f.to_company_id) : name(f.from_company_id)}</td>
                  <td className="px-6 py-3 text-xs">{FLOW_NATURE_LABELS[f.nature] || f.nature}</td>
                  <td className="px-6 py-3 text-right font-mono">{formatEuro(f.amount, true)}</td>
                  <td className="px-6 py-3">
                    <StatusBadge tone={f.status === "realized" ? "success" : f.status === "validated" ? "info" : "warning"}>
                      {FLOW_STATUS_LABELS[f.status]}
                    </StatusBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, amount, count, tone }) {
  const bg = tone === "success" ? "bg-emerald-50" : "bg-sky-50";
  const text = tone === "success" ? "text-emerald-800" : "text-sky-800";
  return (
    <div className={`rounded-2xl border border-border p-5 ${bg}`}>
      <div className={`text-xs font-bold uppercase tracking-widest ${text}`}>{label}</div>
      <div className="mt-1 font-serif text-2xl">{formatEuro(amount, true)}</div>
      <div className="text-xs text-muted-foreground mt-1">{count} flux</div>
    </div>
  );
}
